import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  ArrowRight, ArrowUpRight, Sprout, Users, ShieldCheck, MapPin, Boxes, Sun,
  UserCheck, CalendarClock, ScanLine, Send, Building2, ShoppingCart, TrendingUp,
  Truck, Award, Leaf, Snowflake, Package2, Handshake,
} from "lucide-react";
import { useEffect, useState } from "react";
import { listProducts } from "@/lib/api";
import StrategicAssociations from "@/components/StrategicAssociations";
import EPikChart from "@/components/EPikChart";
import ValueChainWheel from "@/components/ValueChainWheel";

// Round 2 photo swaps — professional agri/tech stock + local orchard shots.
const HERO_IMG = "/photos/IMG-20260715-WA0009.jpg"; // Home hero — full Mosambi tree (approved local)
const WHY_JALNA_IMG = "/photos-r3b/why-jalna-sweet-lime-bg.jpg"; // Round 3 Part B #1
const MOSAMBI_CARD_IMG = "/photos-r3b/products-section-main.jpg"; // Round 3 Part B #2
const FARMER_IMG = "/photos/IMG-20260202-WA0007.jpg"; // farmer-network local
const GET_INVOLVED_IMG = "/photos-r3b/get-involved-bg.jpg"; // Round 3 Part B #4

export default function Home() {
  const { t } = useTranslation();
  const [products, setProducts] = useState([]);
  useEffect(() => { listProducts().then(setProducts).catch(() => setProducts([])); }, []);

  // Round 3 follow-up — public site MUST NOT expose registered farmer/FPO/buyer counts.
  // Those numbers live only in the admin console. No `/api/public/stats` fetch here.

  // Round 2 P1.4 — restore the moving/scrolling strip (marquee) with the same 6 pledges.
  const trustList = [
    t("home.trust_1"), t("home.trust_2"), t("home.trust_3"),
    t("home.trust_4"), t("home.trust_5"), t("home.trust_6"),
  ];

  return (
    <div data-testid="page-home">
      {/* ============================================================
          1. VALUE PROPOSITION — Hero + moving trust strip + value chain
          ============================================================ */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0">
          <img src={HERO_IMG} alt="Jalna Mosambi orchard at dawn" className="w-full h-full object-cover" loading="eager" />
          <div className="absolute inset-0 tr-hero-overlay" />
        </div>

        <div className="relative max-w-7xl mx-auto px-6 lg:px-10 pt-28 pb-32 lg:pt-40 lg:pb-48 grid grid-cols-1 lg:grid-cols-12 gap-10">
          <div className="lg:col-span-8 text-white">
            {/* Round 3 combined §1B — Primary brand identity card moved to top-left of hero
                and enlarged. Positioned directly ABOVE the PROCUREMENT · TRACEABILITY · MARKET
                LINKAGE pill so it reads as the first thing on the page. */}
            <div data-testid="hero-brand-logo" className="tr-rise inline-flex items-center gap-5 rounded-3xl bg-white/90 backdrop-blur-md p-5 md:p-6 pr-7 md:pr-8 shadow-2xl border border-white/50">
              <img
                src="/branding/logo.png"
                alt="TrueRoots Agro Foods LLP official logo"
                className="w-32 h-32 md:w-36 md:h-36 lg:w-40 lg:h-40 object-contain drop-shadow-md"
                loading="eager"
                decoding="async"
              />
              <div className="hidden sm:block">
                <div className="text-[11px] md:text-xs font-bold uppercase tracking-[0.22em] text-[var(--tr-zest)]">TrueRoots Agro Foods LLP</div>
                <div className="mt-1.5 font-display font-extrabold text-xl md:text-2xl lg:text-3xl text-[var(--tr-deep-green)] leading-tight">Soil is the Source,<br />Purity is the Promise.</div>
              </div>
            </div>
            <div className="tr-rise tr-rise-1 mt-5 inline-flex items-center gap-2 rounded-full bg-white/12 backdrop-blur px-3 py-1.5 border border-white/25 text-[11px] tracking-[0.24em] uppercase font-semibold">
              <span className="tr-dot" /> {t("home.platform_pill")}
            </div>
            <h1 className="tr-rise tr-rise-1 mt-6 font-display font-extrabold tracking-tight text-5xl sm:text-6xl lg:text-[88px] leading-[0.92]">
              {t("home.hero_title_a")} <span className="text-[var(--tr-citrus-yellow)]">{t("home.hero_title_b")}</span><br />
              {t("home.hero_title_c")} <span className="italic font-medium">{t("home.hero_title_d")}</span>
            </h1>
            <p className="tr-rise tr-rise-2 mt-6 text-white/85 max-w-2xl text-lg leading-relaxed">
              {t("home.hero_subtitle_v2")}
            </p>

            {/* Round 2 P0.2 — 3 Explore tabs with uniform visual style */}
            <div className="tr-rise tr-rise-3 mt-10 flex flex-wrap items-center gap-3" data-testid="hero-explore-tabs">
              <Link to="/products/jalna-sweet-lime-mosambi" data-testid="hero-explore-mosambi" className="tr-pill-btn tr-explore-tab">
                {t("home.hero_explore")} <ArrowRight className="w-4 h-4" />
              </Link>
              <Link to="/buyers" data-testid="hero-buyer-btn" className="tr-pill-btn tr-explore-tab">
                {t("home.hero_for_buyers")} <ArrowUpRight className="w-4 h-4" />
              </Link>
              <Link to="/farmer-network" data-testid="hero-farmer-btn" className="tr-pill-btn tr-explore-tab">
                {t("home.hero_for_farmers")} <ArrowUpRight className="w-4 h-4" />
              </Link>
            </div>
          </div>

          <div className="hidden lg:block lg:col-span-4 relative">
            <div className="tr-rise tr-rise-4 absolute right-0 top-8 w-64 rounded-2xl bg-[var(--tr-citrus-yellow)] p-5 shadow-2xl">
              <div className="text-[10px] uppercase tracking-[0.22em] text-[var(--tr-deep-green)] font-bold">{t("home.hero_live_label")}</div>
              <div className="mt-1 font-display font-extrabold text-2xl text-[var(--tr-deep-green)] leading-tight">{t("home.hero_live_name")}</div>
            </div>
          </div>
        </div>

        {/* Round 2 P1.4 — Restored moving strip with all 6 pledges (same content as before) */}
        <div className="relative bg-[var(--tr-deep-green)] border-y border-white/10 tr-grain" data-testid="trust-strip">
          <div className="overflow-hidden relative">
            <div className="flex gap-14 py-5 tr-marquee-track whitespace-nowrap" data-testid="trust-strip-marquee">
              {[...trustList, ...trustList, ...trustList].map((tt, i) => (
                <span key={i} className="text-white/80 uppercase tracking-[0.24em] text-xs font-semibold">
                  <span className="text-[var(--tr-citrus-yellow)] mr-2">✦</span>{tt}
                </span>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Round 2 P1.5 — Value chain visual wheel with 8 nodes incl. Consumer */}
      <ValueChainWheel />

      {/* ============================================================
          2. ABOUT — narrative about TrueRoots
          ============================================================ */}
      <section className="relative max-w-7xl mx-auto px-6 lg:px-10 py-24 lg:py-32">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
          <div className="lg:col-span-5">
            <div className="tr-eyebrow">{t("home.about_eyebrow")}</div>
            <h2 className="tr-section-title text-5xl lg:text-7xl mt-4">
              {t("home.about_title_a")}<br /><span className="text-[var(--tr-zest)]">{t("home.about_title_b")}</span><br />{t("home.about_title_c")} <span className="italic font-medium">{t("home.about_title_d")}</span> {t("home.about_title_e")}
            </h2>
          </div>
          <div className="lg:col-span-7">
            <p className="text-lg text-[var(--tr-muted)] leading-relaxed">{t("home.about_body")}</p>
            <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 gap-6">
              {[{ icon: Sprout, k: "ff" }, { icon: Boxes, k: "mc" }, { icon: ShieldCheck, k: "tr" }, { icon: Users, k: "fpo" }].map((f) => (
                <div key={f.k} className="tr-card p-6" data-testid={`about-feature-${f.k}`}>
                  <f.icon className="w-6 h-6 text-[var(--tr-zest)]" />
                  <div className="mt-3 font-display font-bold text-lg text-[var(--tr-deep-green)]">{t(`home.feat_${f.k}_t`)}</div>
                  <div className="mt-2 text-sm text-[var(--tr-muted)] leading-relaxed">{t(`home.feat_${f.k}_d`)}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ============================================================
          3. GI — Brand + GI-495 dedicated section
          ============================================================ */}
      <section className="bg-[var(--tr-sage)]/40 border-y border-[rgba(6,78,59,0.08)]">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-20 lg:py-24 grid grid-cols-1 lg:grid-cols-12 gap-10 items-center" data-testid="home-brand-gi">
          <div className="lg:col-span-5 flex items-center justify-center">
            <div className="relative w-full max-w-md rounded-3xl bg-white border border-[rgba(6,78,59,0.12)] shadow-2xl p-10 lg:p-14 tr-grain">
              <img
                data-testid="home-brand-logo-lg"
                src="/branding/logo.png"
                alt="TrueRoots Agro Foods LLP official logo"
                className="w-full h-auto object-contain"
                loading="eager"
              />
            </div>
          </div>
          <div className="lg:col-span-7">
            <div className="tr-eyebrow">{t("home_p5.brand_eyebrow")}</div>
            <h2 className="mt-4 tr-section-title text-3xl md:text-4xl lg:text-5xl">{t("home_p5.brand_title")}</h2>
            <p className="mt-5 text-[var(--tr-muted)] leading-relaxed max-w-2xl">{t("home_p5.brand_body")}</p>

            <div className="mt-8 rounded-2xl bg-[var(--tr-deep-green)] text-white p-6 md:p-8 relative overflow-hidden tr-grain" data-testid="home-gi-callout">
              <div className="flex items-center gap-3">
                <Award className="w-6 h-6 text-[var(--tr-citrus-yellow)]" />
                <div className="text-[11px] font-bold uppercase tracking-[0.22em] text-white/70">{t("home_p5.gi_eyebrow")}</div>
                <span className="ml-auto inline-block px-3 py-1 rounded-full bg-[var(--tr-citrus-yellow)] text-[var(--tr-deep-green)] text-xs font-extrabold uppercase tracking-[0.14em]">{t("home_p5.gi_tag")}</span>
              </div>
              <h3 className="mt-4 font-display font-extrabold text-2xl md:text-3xl leading-tight">{t("home_p5.gi_title")}</h3>
              <p className="mt-4 text-white/85 leading-relaxed">{t("home_p5.gi_body")}</p>
              <Link to="/products/jalna-sweet-lime-mosambi" data-testid="home-gi-cta" className="mt-6 inline-flex items-center gap-2 font-bold text-[var(--tr-citrus-yellow)] hover:underline">
                {t("home_p5.gi_cta")} <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* ============================================================
          4. DATA — Jalna District Snapshot + e-Pik Pahani charts
          ============================================================ */}
      <section className="bg-white border-b border-[rgba(6,78,59,0.08)]" data-testid="home-snapshot">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-20">
          <div className="max-w-3xl">
            <div className="tr-eyebrow">{t("home_p5.snapshot_eyebrow")}</div>
            <h2 className="mt-4 tr-section-title text-3xl md:text-4xl">{t("home_p5.snapshot_title")}</h2>
            <p className="mt-4 text-[var(--tr-muted)] leading-relaxed">{t("home_p5.snapshot_body")}</p>
          </div>
          <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-5">
            {[
              { k: "total_farmers", v: "18,089", testid: "snap-farmers" },
              { k: "talukas", v: "8", testid: "snap-talukas" },
              { k: "villages", v: "603", testid: "snap-villages" },
            ].map((s) => (
              <div key={s.k} data-testid={s.testid} className="tr-card p-6">
                <div className="font-display font-extrabold text-4xl md:text-5xl text-[var(--tr-deep-green)]">{s.v}</div>
                <div className="mt-2 text-xs uppercase tracking-[0.18em] text-[var(--tr-muted)]">{t(`home_p5.snap_${s.k}`)}</div>
              </div>
            ))}
          </div>
          <div className="mt-10">
            <EPikChart variant="light" />
          </div>
          <div className="mt-6 inline-flex items-start gap-2 text-sm text-[var(--tr-deep-green)] font-semibold" data-testid="snap-ambad-share">
            <MapPin className="w-4 h-4 mt-0.5 text-[var(--tr-zest)]" />
            {t("home_p5.snap_ambad_share")}
          </div>
        </div>
      </section>

      {/* ============================================================
          5. WORKFLOW — merged How-TrueRoots-Works + Workflow (Round 2 P1.7)
          Content from the two duplicate 4-step blocks is consolidated
          into ONE narrative with the richer copy from "How it works".
          ============================================================ */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-20 lg:py-24" data-testid="home-workflow">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-end mb-14">
          <div className="lg:col-span-7">
            <div className="tr-eyebrow">{t("home.how_eyebrow")}</div>
            <h2 className="tr-section-title text-4xl lg:text-6xl mt-4">
              {t("home.how_title_a")}<br />{t("home.how_title_b")}
            </h2>
          </div>
          <p className="lg:col-span-5 text-[var(--tr-muted)] text-lg leading-relaxed">{t("home.how_body")}</p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {[
            { n: "01", icon: UserCheck, k: "s1" },
            { n: "02", icon: CalendarClock, k: "s2" },
            { n: "03", icon: ScanLine, k: "s3" },
            { n: "04", icon: Send, k: "s4" },
          ].map((s) => (
            <div key={s.k} className="tr-card p-8 relative overflow-hidden" data-testid={`how-step-${s.k}`}>
              <span className="absolute -top-6 -right-4 font-display font-black text-[110px] leading-none text-[var(--tr-sage)]/60 select-none">{s.n}</span>
              <s.icon className="w-7 h-7 text-[var(--tr-zest)] relative" />
              <div className="mt-6 font-display font-extrabold text-xl text-[var(--tr-deep-green)] relative">{t(`home.how_${s.k}_t`)}</div>
              <p className="mt-3 text-sm text-[var(--tr-muted)] leading-relaxed relative">{t(`home.how_${s.k}_d`)}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ============================================================
          6. ECOSYSTEM — Services + Quality & Traceability + Products
          What TrueRoots does across the ecosystem.
          ============================================================ */}
      <section className="bg-[var(--tr-bone)]">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-20 lg:py-24" data-testid="home-services">
          <div className="max-w-3xl">
            <div className="tr-eyebrow">{t("home_p5.services_eyebrow")}</div>
            <h2 className="mt-4 tr-section-title text-3xl md:text-4xl">{t("home_p5.services_title")}</h2>
          </div>
          <div className="mt-10 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {[
              { icon: UserCheck, k: "1" }, { icon: Sprout, k: "2" },
              { icon: ShoppingCart, k: "3" }, { icon: Building2, k: "4" },
              { icon: Boxes, k: "5" }, { icon: TrendingUp, k: "6" },
            ].map((s) => (
              <div key={s.k} data-testid={`service-card-${s.k}`} className="tr-card p-6">
                <s.icon className="w-7 h-7 text-[var(--tr-deep-green)]" />
                <div className="mt-4 font-display font-extrabold text-lg text-[var(--tr-deep-green)]">{t(`home_p5.svc_${s.k}_t`)}</div>
                <p className="mt-2 text-sm text-[var(--tr-muted)] leading-relaxed">{t(`home_p5.svc_${s.k}_b`)}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Quality & Traceability — approved as-is in Round 2 */}
        <div className="relative bg-[var(--tr-deep-green)] text-white tr-grain overflow-hidden">
          <div className="max-w-7xl mx-auto px-6 lg:px-10 py-20 lg:py-24 relative">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
              <div className="lg:col-span-5">
                <div className="tr-eyebrow !text-[var(--tr-citrus-yellow)] before:!bg-[var(--tr-citrus-yellow)]">{t("home.quality_eyebrow")}</div>
                <h2 className="font-display font-extrabold text-4xl lg:text-6xl mt-4 tracking-tight leading-[0.95]">{t("home.quality_title_a")}<br />{t("home.quality_title_b")}</h2>
                <p className="mt-6 text-white/75 leading-relaxed">{t("home.quality_body")}</p>
              </div>
              <div className="lg:col-span-7 grid grid-cols-1 md:grid-cols-2 gap-5">
                {[{ n: "01", k: "s1" }, { n: "02", k: "s2" }, { n: "03", k: "s3" }, { n: "04", k: "s4" }].map((s) => (
                  <div key={s.n} className="rounded-2xl border border-white/15 p-6 bg-white/[0.04] backdrop-blur">
                    <div className="font-display text-[var(--tr-citrus-yellow)] text-3xl font-extrabold">{s.n}</div>
                    <div className="mt-3 font-display font-bold text-lg">{t(`home.quality_${s.k}_t`)}</div>
                    <div className="mt-2 text-sm text-white/70 leading-relaxed">{t(`home.quality_${s.k}_d`)}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* WHY JALNA — replaces the unfinished background image with a professional citrus grove photo (Round 2 P1.6) */}
        <div className="relative bg-white border-t border-[rgba(6,78,59,0.08)]">
          <div className="max-w-7xl mx-auto px-6 lg:px-10 py-20 lg:py-24">
            <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-12">
              <div>
                <div className="tr-eyebrow">{t("home.why_eyebrow")}</div>
                <h2 className="tr-section-title text-4xl lg:text-6xl mt-4">{t("home.why_title_a")}<br />{t("home.why_title_b")}</h2>
              </div>
              <Link to="/products/jalna-sweet-lime-mosambi" data-testid="why-mosambi-cta" className="tr-pill-btn tr-btn-primary">
                {t("home.why_cta")} <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
            <div className="tr-bento">
              <div className="col-span-12 lg:col-span-7 rounded-3xl overflow-hidden relative h-[420px] tr-card p-0">
                <img src={WHY_JALNA_IMG} alt="Sweet lime grove in Marathwada" className="w-full h-full object-cover" loading="lazy" />
                <div className="absolute inset-0 bg-gradient-to-tr from-[var(--tr-deep-green)]/60 via-[var(--tr-deep-green)]/10 to-transparent" />
                <div className="absolute bottom-6 left-6 right-6 text-white">
                  <div className="text-[10px] uppercase tracking-[0.24em] font-bold">{t("home.why_location")}</div>
                  <div className="mt-2 font-display font-extrabold text-3xl leading-tight max-w-md">{t("home.why_headline")}</div>
                </div>
              </div>
              <div className="col-span-12 lg:col-span-5 grid grid-cols-1 gap-6">
                <div className="tr-card p-8">
                  <Sun className="w-6 h-6 text-[var(--tr-zest)]" />
                  <div className="mt-3 font-display font-extrabold text-xl">{t("home.why_cycles_t")}</div>
                  <p className="text-sm text-[var(--tr-muted)] mt-2 leading-relaxed">{t("home.why_cycles_d")}</p>
                </div>
                <div className="tr-card p-8">
                  <MapPin className="w-6 h-6 text-[var(--tr-zest)]" />
                  <div className="mt-3 font-display font-extrabold text-xl">{t("home.why_single_t")}</div>
                  <p className="text-sm text-[var(--tr-muted)] mt-2 leading-relaxed">{t("home.why_single_d")}</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Products — approved as-is */}
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-20 lg:py-24">
          <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-12">
            <div>
              <div className="tr-eyebrow">{t("home.products_eyebrow")}</div>
              <h2 className="tr-section-title text-4xl lg:text-6xl mt-4">{t("home.products_title_a")}<br />{t("home.products_title_b")}</h2>
            </div>
            <p className="max-w-md text-[var(--tr-muted)]">{t("home.products_body")}</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {products.map((p) => (
              <Link key={p.id} to={`/products/${p.slug}`} data-testid={`product-card-${p.slug}`} className="tr-card overflow-hidden group tr-nolink">
                <div className="aspect-[4/3] overflow-hidden">
                  <img src={p.slug === "jalna-sweet-lime-mosambi" ? MOSAMBI_CARD_IMG : p.image_url} alt={p.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" loading="lazy" />
                </div>
                <div className="p-6">
                  <div className="text-[10px] uppercase tracking-[0.22em] text-[var(--tr-zest)] font-bold">{p.category}</div>
                  <div className="mt-2 font-display font-extrabold text-xl text-[var(--tr-deep-green)]">{p.name}</div>
                  <p className="mt-3 text-sm text-[var(--tr-muted)] leading-relaxed line-clamp-3">{p.tagline || p.short_description}</p>
                  <div className="mt-5 inline-flex items-center gap-1 text-sm font-semibold text-[var(--tr-deep-green)]">
                    {t("common.explore")} <ArrowRight className="w-4 h-4" />
                  </div>
                </div>
              </Link>
            ))}
          {/* Round 3 Part A #1.4 — Commodities coming-soon order:
              Fruits (live) → Food Grains → Millets → Spices → Edible Oil. */}
          {[
            t("home.products_slot_grains"),
            t("home.products_slot_mil"),
            t("home.products_slot_spi"),
            t("home.products_slot_oil"),
          ].map((c) => (
              <div key={c} className="rounded-3xl border border-dashed border-[rgba(6,78,59,0.25)] p-8 flex flex-col justify-between min-h-[280px] bg-[rgba(255,255,255,0.5)]">
                <div>
                  <div className="text-[10px] uppercase tracking-[0.22em] text-[var(--tr-muted)] font-bold">{t("common.coming_soon")}</div>
                  <div className="mt-2 font-display font-extrabold text-2xl text-[var(--tr-deep-green)]">{c}</div>
                </div>
                <p className="text-sm text-[var(--tr-muted)] leading-relaxed">{t("home.products_slot_note")}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ============================================================
          7. STRATEGIC ASSOCIATION — RFPCL + Mosambi Sangh
          ============================================================ */}
      <StrategicAssociations />

      {/* ============================================================
          8. FARMER NETWORK — approved as-is (Round 2 P0.9)
          ============================================================ */}
      <section className="relative max-w-7xl mx-auto px-6 lg:px-10 py-24 lg:py-32">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-6 relative">
            <div className="rounded-[28px] overflow-hidden aspect-[4/5] border border-[rgba(6,78,59,0.15)]">
              <img src={FARMER_IMG} alt="Jalna Mosambi farmer" className="w-full h-full object-cover" loading="lazy" />
            </div>
            {/* Round 3 follow-up — removed the "45+ Villages" and any registered-count
                callouts. Public pages must not expose farmer/FPO/buyer registration figures. */}
          </div>
          <div className="lg:col-span-6">
            <div className="tr-eyebrow">{t("home.network_eyebrow")}</div>
            <h2 className="tr-section-title text-4xl lg:text-6xl mt-4">{t("home.network_title_a")}<br />{t("home.network_title_b")}</h2>
            <p className="mt-6 text-lg text-[var(--tr-muted)] leading-relaxed">{t("home.network_body")}</p>
            <ul className="mt-8 space-y-3 text-[var(--tr-deep-green)]">
              {[1, 2, 3, 4].map((n) => (
                <li key={n} className="flex items-start gap-3">
                  <span className="mt-2 tr-dot" />
                  <span>{t(`home.network_b${n}`)}</span>
                </li>
              ))}
            </ul>
            <div className="mt-10 flex flex-wrap gap-3">
              <Link to="/farmer-interest" data-testid="home-farmer-cta" className="tr-pill-btn tr-btn-primary">
                {t("home.register_your_farm")} <ArrowRight className="w-4 h-4" />
              </Link>
              <Link to="/fpo-partnership" data-testid="home-fpo-cta" className="tr-pill-btn tr-btn-outline">
                {t("home.network_fpo")}
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* ============================================================
          9. GET INVOLVED — final CTA
          ============================================================ */}
      <section className="relative max-w-7xl mx-auto px-6 lg:px-10 pb-24">
        <div className="relative rounded-3xl overflow-hidden border border-[rgba(6,78,59,0.08)]">
          {/* Round 3 Part A #1.3 — improved contrast (darker + opaque backdrop under text) */}
          <img src={GET_INVOLVED_IMG} alt="Community harvest" className="absolute inset-0 w-full h-full object-cover" loading="lazy" />
          <div className="absolute inset-0 bg-[var(--tr-deep-green)]/85" />
          <div className="absolute inset-0 bg-gradient-to-r from-[var(--tr-deep-green)] via-[var(--tr-deep-green)]/90 to-[var(--tr-deep-green)]/70" />
          <div className="relative p-10 lg:p-16 text-white grid grid-cols-1 lg:grid-cols-12 gap-10 items-center" data-testid="get-involved-content">
            <div className="lg:col-span-8">
              <div className="tr-eyebrow !text-[var(--tr-citrus-yellow)] before:!bg-[var(--tr-citrus-yellow)]">{t("home.cta_eyebrow")}</div>
              <h2 className="font-display font-extrabold text-4xl lg:text-6xl mt-4 tracking-tight leading-[0.95]">{t("home.cta_title_a")}<br />{t("home.cta_title_b")}</h2>
            </div>
            <div className="lg:col-span-4 flex flex-col gap-3">
              <Link to="/farmer-interest" data-testid="cta-farmer" className="tr-pill-btn tr-btn-zest">{t("home.register_your_farm")}</Link>
              <Link to="/fpo-partnership" data-testid="cta-fpo" className="tr-pill-btn bg-white/15 text-white border border-white/25 hover:bg-white/25">{t("home.cta_fpo")}</Link>
              <Link to="/buyers" data-testid="cta-buyer" className="tr-pill-btn bg-white text-[var(--tr-deep-green)] hover:bg-white/90">{t("home.cta_buyer")}</Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
